import { Container } from "@/components/molecules/Container";
import { Inner } from "@/components/molecules/Inner";
import { BreadCrumb } from "@/components/organisms/BreadCrumb";
import Careers from "@/components/organisms/Careers";
import Contact from "@/components/organisms/Contact";
import { Footer } from "@/components/templates/Footer";
import { Header } from "@/components/templates/Header";
import Layout from "@/components/templates/Layout";
import Meta from "@/components/templates/Meta";
import Image from "next/image";
import { Br } from "@/components/molecules/Br";

export default function ProductCloudCMSPage() {
  const isHeaderRelative = true;
  return (
    <>
      <Meta
        pageTitle={"クラウド型CMSサービス"}
        pageDescription={
          "トッカシステムズのクラウド型CMSサービスは、ブラウザから簡単にWebサイトのコンテンツを更新できるヘッドレスCMSです。専門知識不要で、直感的な操作でコンテンツを管理できます。"
        }
        pagePath={"https://tocca.systems/product/cloud-cms/"}
      />
      <Layout className="bg-gray-50">
        <Header isRelative={isHeaderRelative} />
        <main className="z-10 flex-1">
          <Container>
            <BreadCrumb
              lists={[
                {
                  name: "TOP",
                  path: "/",
                },
                {
                  name: "Product",
                  path: "/product/",
                },
                {
                  name: "クラウド型CMSサービス",
                },
              ]}
            />
            <Inner className="max-w-full pt-0 md:pt-5">
              <figure className="relative mx-auto box-border block aspect-[1272/360] w-full max-w-7xl overflow-hidden rounded-lg">
                <Image
                  src="/images/product/cloud-cms-banner.png"
                  alt="クラウド型CMSサービス"
                  quality={80}
                  layout="fill"
                  objectFit="cover"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40">
                  <h1 className="text-center font-serif text-3xl font-bold text-white drop-shadow-lg md:text-5xl">
                    クラウド型CMSサービス
                  </h1>
                </div>
              </figure>
              <div className="mx-auto box-border flex max-w-7xl flex-col items-start gap-8 bg-white py-8 px-6 sm:px-8 md:gap-12 md:px-12 md:py-16">
                <section className="w-full">
                  <h2 className="mb-6 border-b-2 border-pri pb-2 font-serif text-2xl font-bold text-pri md:text-3xl">
                    サービス概要
                  </h2>
                  <p className="leading-relaxed text-txt-base fluid-text-[14px,18px]">
                    クラウド型CMSサービスは、Webサイトのコンテンツ管理を簡単にするヘッドレスCMSです。
                    <Br pc />
                    ブラウザからアクセスするだけで、お知らせやイベント情報、画像などを自由に編集・公開できます。
                    <Br pc />
                    専門的な知識は一切不要。直感的な操作で、誰でも簡単にWebサイトを更新できます。
                  </p>
                  <div className="mt-8 grid gap-6 md:grid-cols-2">
                    <figure className="overflow-hidden rounded-lg border border-bdc-light">
                      <Image
                        src="/images/product/cms-list-view.png"
                        alt="コンテンツ一覧画面"
                        width={1400}
                        height={900}
                        quality={80}
                        className="w-full"
                      />
                      <figcaption className="bg-gray-50 p-3 text-center text-sm text-txt-light">
                        コンテンツ一覧画面
                      </figcaption>
                    </figure>
                    <figure className="overflow-hidden rounded-lg border border-bdc-light">
                      <Image
                        src="/images/product/cms-edit-view.png"
                        alt="コンテンツ編集画面"
                        width={1400}
                        height={900}
                        quality={80}
                        className="w-full"
                      />
                      <figcaption className="bg-gray-50 p-3 text-center text-sm text-txt-light">
                        コンテンツ編集画面
                      </figcaption>
                    </figure>
                  </div>
                </section>

                <section className="w-full">
                  <h2 className="mb-6 border-b-2 border-pri pb-2 font-serif text-2xl font-bold text-pri md:text-3xl">
                    特徴
                  </h2>
                  <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    <div className="rounded-lg border border-bdc-light bg-gray-50 p-6">
                      <h3 className="mb-3 font-bold text-pri fluid-text-[16px,20px]">
                        クラウドベース
                      </h3>
                      <p className="text-txt-base fluid-text-[13px,15px]">
                        サーバーの管理や保守は不要。インターネット環境があれば、どこからでもコンテンツを管理できます。
                      </p>
                    </div>
                    <div className="rounded-lg border border-bdc-light bg-gray-50 p-6">
                      <h3 className="mb-3 font-bold text-pri fluid-text-[16px,20px]">
                        直感的な操作
                      </h3>
                      <p className="text-txt-base fluid-text-[13px,15px]">
                        WIXのような使いやすいインターフェース。専門知識がなくても、簡単にコンテンツを編集・公開できます。
                      </p>
                    </div>
                    <div className="rounded-lg border border-bdc-light bg-gray-50 p-6">
                      <h3 className="mb-3 font-bold text-pri fluid-text-[16px,20px]">
                        リアルタイム更新
                      </h3>
                      <p className="text-txt-base fluid-text-[13px,15px]">
                        編集した内容は即座にWebサイトに反映。タイムリーな情報発信が可能です。
                      </p>
                    </div>
                    <div className="rounded-lg border border-bdc-light bg-gray-50 p-6">
                      <h3 className="mb-3 font-bold text-pri fluid-text-[16px,20px]">
                        セキュリティ対策
                      </h3>
                      <p className="text-txt-base fluid-text-[13px,15px]">
                        SSL対応、定期バックアップ、アクセス権限管理など、セキュリティ対策も万全です。
                      </p>
                    </div>
                    <div className="rounded-lg border border-bdc-light bg-gray-50 p-6">
                      <h3 className="mb-3 font-bold text-pri fluid-text-[16px,20px]">
                        柔軟なカスタマイズ
                      </h3>
                      <p className="text-txt-base fluid-text-[13px,15px]">
                        独自のサブサービスやデータベースを別サーバに構築し、CMSとシームレスに認証連携が可能。予約システムや申込機能など、お客様のビジネスに合わせた拡張ができます。
                      </p>
                    </div>
                    <div className="rounded-lg border border-bdc-light bg-gray-50 p-6">
                      <h3 className="mb-3 font-bold text-pri fluid-text-[16px,20px]">
                        ヘッドレスCMS
                      </h3>
                      <p className="text-txt-base fluid-text-[13px,15px]">
                        フロントエンドとバックエンドが分離されているため、デザインの自由度が高く、高速なサイト表示を実現します。
                      </p>
                    </div>
                  </div>
                </section>

                <section className="w-full">
                  <h2 className="mb-6 border-b-2 border-pri pb-2 font-serif text-2xl font-bold text-pri md:text-3xl">
                    こんな方におすすめ
                  </h2>
                  <ul className="list-inside list-disc space-y-3 text-txt-base fluid-text-[14px,18px]">
                    <li>Webサイトの更新を自社で行いたい企業・団体様</li>
                    <li>専門知識がなくても簡単に更新できるCMSをお探しの方</li>
                    <li>サーバー管理の手間を省きたい方</li>
                    <li>予約システムや申込機能など、独自の機能が必要な方</li>
                    <li>セキュリティを重視したサイト運営をしたい方</li>
                  </ul>
                </section>
              </div>
            </Inner>
          </Container>
        </main>
        <Contact>
          クラウド型CMSサービスについて、お気軽にお問い合わせください。
          <br />
          お客様のニーズに合わせた最適なご提案をいたします。
        </Contact>
        <Careers />
        <Footer />
      </Layout>
    </>
  );
}
